import { DisplayAmount } from '@eventbrite/eds-read-more';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import React, { FunctionComponent, ReactNode } from 'react';
import {
    CSS_TICKET_DISABLED,
    SPEC_TICKET_CONTENT,
} from '../../../containers/ticketSelection/constants';
import TicketAvailabilityField, {
    TicketAvailabilityFieldProps,
} from '../../../containers/ticketSelection/TicketAvailabilityField';
import {
    shouldDisableTicketContent,
    shouldShowPrice,
} from '../../../containers/ticketSelection/utils/ticketUtils';
import { UrgencySignals } from '../../../models/urgencySignals';
import { getUndiscountedBOGOTicketPrice } from '../../../utils/bogoTickets';
import { isDonationTicket } from '../../../utils/tickets';
import { NOT_AVAILABLE_FOR_SALE_STATUSES } from '../constants';
import PriceInformation, { PriceInformationProps } from '../PriceInformation';
import PromoCodePills from '../PromoCodePills';
import { RemainingTicketsProps } from '../RemainingTickets';
import TicketCardGraphicWrapper from '../TicketCardGraphicWrapper';
import TicketCardStatus, {
    TicketCardStatusTextSize,
} from '../TicketCardStatus';
import TicketDescription from '../TicketDescription';
import TicketPaymentConstraints, {
    TicketPaymentConstraintsProps,
} from '../TicketPaymentConstraints';
import TicketRemainingUrgencySignal from '../TicketRemainingTicketsCount/TicketRemainingUrgencySignal';
import './TicketContent.scss';

export enum DonationFeeTaxPriceImpact {
    feePassedOn = 'feePassedOn',
    feeAbsorbed = 'feeAbsorbed',
    feeAndTaxPassedOn = 'feeAndTaxPassedOn',
    feeAbsorbedTaxPassedOn = 'feeAbsorbedTaxPassedOn',
    feePassedOnTaxAbsorbed = 'feePassedOnTaxAbsorbed',
    feeAndTaxAbsorbed = 'feeAndTaxAbsorbed',
}

export const DONATION_FEE_TAX_COPIES: Record<
    DonationFeeTaxPriceImpact,
    GenericLazyString
> = {
    [DonationFeeTaxPriceImpact.feePassedOn]: gettext(
        'Fees will be calculated before you place your order.',
    ),
    [DonationFeeTaxPriceImpact.feeAbsorbed]: gettext(
        'Fees will be deducted from your donation amount.',
    ),
    [DonationFeeTaxPriceImpact.feeAndTaxPassedOn]: gettext(
        'Fees and taxes will be calculated before you place your order.',
    ),
    [DonationFeeTaxPriceImpact.feeAndTaxAbsorbed]: gettext(
        'Fees and taxes will be deducted from your donation amount.',
    ),
    [DonationFeeTaxPriceImpact.feeAbsorbedTaxPassedOn]: gettext(
        'Fees will be deducted from your donation amount but taxes will be calculated before you place your order.',
    ),
    [DonationFeeTaxPriceImpact.feePassedOnTaxAbsorbed]: gettext(
        'Taxes will be deducted from your donation amount but fees will be calculated before you place your order.',
    ),
};

const getDonationFeeTaxPriceImpactWhenThereAreNoTaxes = (includeFee: boolean) =>
    includeFee
        ? DonationFeeTaxPriceImpact.feeAbsorbed
        : DonationFeeTaxPriceImpact.feePassedOn;

const getDonationFeeTaxPriceImpactWhenTaxesAreAbsorbed = (
    includeFee: boolean,
) =>
    includeFee
        ? DonationFeeTaxPriceImpact.feeAndTaxAbsorbed
        : DonationFeeTaxPriceImpact.feePassedOnTaxAbsorbed;

const getDonationFeeTaxPriceImpactWhenTaxesArePassedOn = (
    includeFee: boolean,
) =>
    includeFee
        ? DonationFeeTaxPriceImpact.feeAbsorbedTaxPassedOn
        : DonationFeeTaxPriceImpact.feeAndTaxPassedOn;

const getDonationFeeTaxPriceImpact = ({
    includeFee,
    includeTax,
}: {
    includeFee: boolean;
    includeTax: boolean | null;
}): DonationFeeTaxPriceImpact => {
    if (includeTax === null) {
        return getDonationFeeTaxPriceImpactWhenThereAreNoTaxes(includeFee);
    }

    return includeTax
        ? getDonationFeeTaxPriceImpactWhenTaxesAreAbsorbed(includeFee)
        : getDonationFeeTaxPriceImpactWhenTaxesArePassedOn(includeFee);
};

// Generate fee and tax text copy for donation ticket.
const getDonationFeeTaxText = (
    includeFee: boolean,
    includeTax: boolean | null,
) => {
    const donationFeeTaxPriceImpact = getDonationFeeTaxPriceImpact({
        includeFee,
        includeTax,
    });

    return DONATION_FEE_TAX_COPIES[donationFeeTaxPriceImpact];
};

//TODO Migrate to a new component file.
export const TicketFeeTaxDescription: FunctionComponent<
    Pick<TicketContentProps, 'category' | 'includeFee' | 'includeTax'> & {
        canBeSelected: boolean;
    }
> = ({ canBeSelected, category, includeFee, includeTax }) => {
    if (
        !canBeSelected ||
        !isDonationTicket({
            category,
        })
    ) {
        return null;
    }

    const donationFeeText = getDonationFeeTaxText(includeFee, includeTax);

    return <span data-spec="donation-fee-text">{donationFeeText}</span>;
};

export interface TicketContentProps
    extends Pick<
            TicketAvailabilityFieldProps,
            | 'category'
            | 'currencyFormat'
            | 'currencySymbol'
            | 'displayPrice'
            | 'donationAmount'
            | 'hideSaleDates'
            | 'id'
            | 'isCurrencySuffix'
            | 'isFormSubmitting'
            | 'isTicketTiered'
            | 'name'
            | 'onDonationChange'
            | 'onJoinWaitlist'
            | 'onQuantityChange'
            | 'salesEnd'
            | 'salesStart'
            | 'selectedQuantity'
            | 'showJoinWaitlist'
            | 'statusKey'
            | 'ticketOptionsRange'
            | 'ticketReseller'
            | 'variantId'
        >,
        Pick<PriceInformationProps, 'feeAndTax' | 'primaryPrice'>,
        Pick<TicketPaymentConstraintsProps, 'paymentConstraints'> {
    /**
     * Description for a the given ticket. If not included, no description will be rendered.
     */
    description: ReactNode;
    /**
     * A node can be placed at the head of the ticket content card. E.g. ticket image, color cirle, icon, etc.
     */
    graphic: ReactNode;
    hideSelect: boolean;
    shouldHideStatus: boolean;
    showDiscountedPill: boolean;
    showUnlockedPill: boolean;
    includeFee: boolean;
    includeTax: boolean | null;
    quantityRemaining: RemainingTicketsProps['quantityRemaining'] | null;
    urgencySignals: UrgencySignals | null;
    cost?: {
        currency: string;
        display: string | GenericLazyString;
        value: number;
    };
    isBOGOTicket: boolean;
}

export const TicketContent: FunctionComponent<TicketContentProps> = ({
    category,
    cost,
    currencyFormat,
    currencySymbol,
    description,
    displayPrice,
    donationAmount,
    feeAndTax,
    graphic,
    hideSaleDates,
    hideSelect,
    id,
    includeFee,
    includeTax,
    isBOGOTicket,
    isCurrencySuffix,
    isFormSubmitting,
    isTicketTiered,
    name,
    onDonationChange,
    onJoinWaitlist,
    onQuantityChange,
    paymentConstraints,
    primaryPrice,
    quantityRemaining,
    salesEnd,
    salesStart,
    selectedQuantity,
    shouldHideStatus,
    showDiscountedPill,
    showJoinWaitlist,
    showUnlockedPill,
    statusKey,
    ticketOptionsRange,
    ticketReseller,
    urgencySignals,
    variantId,
}) => {
    const dataAutomation = `ticket-card-content-${id}`;
    const canBeSelected =
        !hideSelect && !includes(NOT_AVAILABLE_FOR_SALE_STATUSES, statusKey);

    const paymentConstraintsDisplay = !isEmpty(paymentConstraints) ? (
        <TicketPaymentConstraints paymentConstraints={paymentConstraints} />
    ) : null;

    const originalPrice =
        isBOGOTicket && cost && !primaryPrice
            ? getUndiscountedBOGOTicketPrice({
                  currency: cost.currency,
                  value: cost.value,
                  currencyFormat,
              })
            : primaryPrice;

    const priceInfo = shouldShowPrice({
        category,
        showJoinWaitlist,
        statusKey,
    }) ? (
        <PriceInformation
            primaryPrice={originalPrice}
            price={displayPrice}
            feeAndTax={feeAndTax}
        />
    ) : null;

    const hideStatus = shouldHideStatus || hideSaleDates;

    const ticketGraphicWrapper = graphic ? (
        <TicketCardGraphicWrapper graphic={graphic} />
    ) : null;
    const remainingTicketsContent = quantityRemaining ? (
        <TicketRemainingUrgencySignal
            urgencySignals={urgencySignals}
            numTicketsRemaining={quantityRemaining}
            className="remaining-tickets-pill"
        />
    ) : null;
    const ticketStatusContent = canBeSelected ? (
        <TicketCardStatus
            isTicketTiered={isTicketTiered}
            salesEnd={salesEnd}
            salesStart={salesStart}
            shouldHideStatus={hideStatus}
            statusKey={statusKey}
            textSize={TicketCardStatusTextSize.medium}
        />
    ) : null;

    const ticketCardBodyClass = classNames(
        'ticket-card-content__body',
        'eds-g-cell eds-g-cell-12-12',
        {
            [`${CSS_TICKET_DISABLED}`]: shouldDisableTicketContent({
                showJoinWaitlist,
                statusKey,
                ticketReseller,
            }),
        },
    );
    const ticketCardPrimaryContentClass = classNames(
        'eds-ticket-card-content eds-ticket-display-card-content eds-l-pad-all-0',
    );

    const ticketNamePaddingBottom = isBOGOTicket ? 'eds-l-pad-bot-2' : '';

    const isDonation = isDonationTicket({
        category,
    });

    const ticketNameContainerClassSuffix = isDonation ? '-donation' : '';
    const ticketNameContainerClass = `ticket-title-container${ticketNameContainerClassSuffix} ${ticketNamePaddingBottom}`;

    const isDiscounted = showDiscountedPill && !!cost && !isDonation;

    return (
        <div className="eds-ticket-card-content__container eds-align--left eds-l-pad-vert-4">
            {ticketGraphicWrapper}
            <div
                className={ticketCardBodyClass}
                data-automation={dataAutomation}
                data-spec={SPEC_TICKET_CONTENT}
                data-testid={SPEC_TICKET_CONTENT}
            >
                <div className={ticketCardPrimaryContentClass}>
                    <div className={ticketNameContainerClass}>
                        <h3 className="ticket-title">{name}</h3>
                        <div>
                            {paymentConstraintsDisplay}
                            <div className="eds-l-mar-bot-1">{priceInfo}</div>
                            <TicketFeeTaxDescription
                                canBeSelected={canBeSelected}
                                category={category}
                                includeFee={includeFee}
                                includeTax={includeTax}
                            />
                        </div>
                        <PromoCodePills
                            isBOGO={isBOGOTicket}
                            isDiscounted={isDiscounted}
                            isUnlocked={showUnlockedPill}
                            className={''}
                        />
                    </div>
                    <TicketAvailabilityField
                        category={category}
                        canBeSelected={canBeSelected}
                        currencyFormat={currencyFormat}
                        currencySymbol={currencySymbol}
                        displayPrice={displayPrice}
                        donationAmount={donationAmount}
                        hideSaleDates={shouldHideStatus}
                        id={id}
                        isCurrencySuffix={isCurrencySuffix}
                        isFormSubmitting={isFormSubmitting}
                        isTicketTiered={isTicketTiered}
                        name={name}
                        onDonationChange={onDonationChange}
                        onJoinWaitlist={onJoinWaitlist}
                        onQuantityChange={onQuantityChange}
                        salesEnd={salesEnd}
                        salesStart={salesStart}
                        selectedQuantity={selectedQuantity}
                        showJoinWaitlist={showJoinWaitlist}
                        statusKey={statusKey}
                        ticketOptionsRange={ticketOptionsRange}
                        ticketReseller={ticketReseller}
                        variantId={variantId}
                    />
                </div>
                <div className="ticket-card__secondary-content">
                    {remainingTicketsContent}
                    {ticketStatusContent}
                    <TicketDescription
                        collapsedDisplayAmount={DisplayAmount.small}
                    >
                        {description}
                    </TicketDescription>
                </div>
            </div>
        </div>
    );
};

export default TicketContent;
